import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Calendar } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { SERVICES, TIME_SLOTS } from "../lib/brand";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Booking() {
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [service, setService] = useState("");
  const [form, setForm] = useState({ name: "", email: "", business: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!date || !time || !service) {
      toast.error("Please choose a date, time, and the service you're interested in.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name,
        email: form.email,
        business: form.business || null,
        service_interest: service,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
        notes: form.notes || null,
      });
      setDone(true);
      toast.success("Request received. We will confirm by email within one working day.");
    } catch (err) {
      toast.error("Something did not go through. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const isPastOrWeekend = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    const day = d.getDay();
    return day === 0 || day === 6;
  };

  if (done) {
    return (
      <section className="mx-auto max-w-[820px] px-6 lg:px-10 py-16 lg:py-24" data-testid="booking-success">
        <div className="inline-flex items-center gap-2 text-bos-primary mb-6">
          <CheckCircle2 size={20} />
          <span className="bos-eyebrow">Request received</span>
        </div>
        <h1 className="text-4xl lg:text-[52px] font-medium text-bos-navy tracking-tight leading-[1.1]">
          Thank you. We will be in touch shortly.
        </h1>
        <p className="mt-6 text-[16px] text-bos-charcoal/85 leading-[1.75]">
          A short email will land in your inbox within one working day to
          confirm the time. If anything changes before then, just write back
          and we will reschedule together.
        </p>
        <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-lg">
          <a
            href="/services"
            data-testid="booking-success-services"
            className="px-6 py-3 rounded-full bg-bos-ice text-bos-navy hover:bg-bos-light/60 transition-colors text-center text-[14px]"
          >
            Explore the services
          </a>
          <a
            href="/insights"
            data-testid="booking-success-insights"
            className="px-6 py-3 rounded-full bg-bos-navy text-bos-white hover:bg-bos-primary transition-colors text-center text-[14px]"
          >
            Read recent insights
          </a>
        </div>
      </section>
    );
  }

  return (
    <div data-testid="booking-page">
      <section className="border-b border-bos-ice">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-20 lg:pt-24 pb-16">
          <p className="bos-eyebrow">Book a discovery call</p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[60px] font-medium text-bos-navy tracking-tight leading-[1.08] max-w-4xl">
            One hour. <span className="text-bos-primary">No pitch, no pressure.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-[17px] text-bos-charcoal/85 leading-[1.75]">
            Choose a date and time that suits you. The first call is on us. We
            will spend it listening to where you are, asking a few honest
            questions, and saying so if we are not the right fit.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-24">
        <form
          onSubmit={onSubmit}
          className="grid lg:grid-cols-[1fr_1fr] gap-12"
          data-testid="booking-form"
        >
          <div className="space-y-10">
            <div>
              <p className="bos-eyebrow">01 · Choose a date</p>
              <div
                className="mt-5 bg-bos-white border border-bos-ice rounded-sm p-3 inline-block"
                data-testid="booking-calendar"
              >
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={isPastOrWeekend}
                  initialFocus
                />
              </div>
              <p className="mt-3 text-[12px] text-bos-charcoal/60">
                Weekdays only. Times are in your local timezone.
              </p>
            </div>

            <div>
              <p className="bos-eyebrow">02 · Choose a time</p>
              <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 gap-2 max-w-md" data-testid="booking-time-slots">
                {TIME_SLOTS.map((t) => (
                  <button
                    type="button"
                    key={t}
                    data-testid={`booking-slot-${t.replace(":", "")}`}
                    onClick={() => setTime(t)}
                    className={`px-3 py-2.5 rounded-sm border text-[14px] transition-colors ${
                      time === t
                        ? "bg-bos-navy text-bos-white border-bos-navy"
                        : "bg-bos-white text-bos-charcoal border-bos-ice hover:border-bos-mid"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="bos-eyebrow">03 · Which service interests you</p>
              <div className="mt-5 max-w-md" data-testid="booking-service-select">
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger data-testid="booking-service-trigger" className="bg-bos-white border-bos-ice">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.id} value={s.name} data-testid={`booking-service-option-${s.id}`}>
                        {s.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="Not sure yet" data-testid="booking-service-option-not-sure">
                      Not sure yet
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="space-y-5 bg-bos-ice/40 border border-bos-light/40 rounded-sm p-8" data-testid="booking-details">
            <p className="bos-eyebrow">04 · Your details</p>

            <div>
              <label className="block text-[12px] text-bos-charcoal/70 mb-1.5" htmlFor="bk-name">Your name</label>
              <input
                id="bk-name"
                data-testid="booking-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[14px]"
              />
            </div>
            <div>
              <label className="block text-[12px] text-bos-charcoal/70 mb-1.5" htmlFor="bk-email">Email</label>
              <input
                id="bk-email"
                data-testid="booking-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[14px]"
              />
            </div>
            <div>
              <label className="block text-[12px] text-bos-charcoal/70 mb-1.5" htmlFor="bk-business">Business or role</label>
              <input
                id="bk-business"
                data-testid="booking-business"
                value={form.business}
                onChange={(e) => setForm({ ...form, business: e.target.value })}
                className="w-full px-4 py-3 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[14px]"
              />
            </div>
            <div>
              <label className="block text-[12px] text-bos-charcoal/70 mb-1.5" htmlFor="bk-notes">What would you like to discuss</label>
              <textarea
                id="bk-notes"
                data-testid="booking-notes"
                rows={5}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full px-4 py-3 bg-bos-white border border-bos-ice focus:border-bos-mid outline-none rounded-sm text-[14px] resize-none"
                placeholder="A sentence or two on what is on your mind."
              />
            </div>

            <div className="pt-2 text-[13px] text-bos-charcoal/80 leading-[1.6]">
              {date && time && service ? (
                <div className="flex items-center gap-2 text-bos-navy" data-testid="booking-summary">
                  <CheckCircle2 size={16} className="text-bos-primary" />
                  <span>
                    {format(date, "EEEE, d MMMM yyyy")} · {time} · {service}
                  </span>
                </div>
              ) : (
                <span>Choose a date, time, and service to enable the request.</span>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting || !date || !time || !service}
              data-testid="booking-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-bos-navy text-bos-white py-3.5 rounded-full hover:bg-bos-primary transition-colors disabled:opacity-50 text-[14px]"
            >
              {submitting ? "Sending request…" : "Request this call"}
              <ArrowRight size={14} />
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
